import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../layout/PublicNav";
import PublicFooter from "../layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../SMSRecruiterCard";
import PageMeta from "../seo/PageMeta";
import { CHECKLISTS } from "../../data/checklistsData";
import {
  HeartHandshake, Stethoscope, Microscope, Search, X, ArrowRight, ArrowUpRight,
  ClipboardCheck, ShieldCheck, Save, Sparkles, AlertCircle,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Shared catalog: nurse / therapist / other                         */
/*  Renders premium hero + search + category filter + card grid       */
/*  Each card links to /compliance/checklists/:type/:slug             */
/* ------------------------------------------------------------------ */

const TYPE_META = {
  nurse: {
    Icon: HeartHandshake,
    eyebrow: "Nurse Skills Checklist",
    breadcrumb: "Nurse",
    heroAccent: "text-trital-navy",
    h1: (
      <>
        Pick a nurse checklist to <span className="text-trital-navy">begin.</span>
      </>
    ),
    sub: "50+ specialty modules — from Med-Surg, ICU, OR, L&D, ED, and Telemetry to Wound Care and Travel Nursing. Self-rate, attest, and submit for recruiter review.",
  },
  therapist: {
    Icon: Stethoscope,
    eyebrow: "Therapist Skills Checklist",
    breadcrumb: "Therapist",
    heroAccent: "text-trital-navy",
    h1: (
      <>
        Pick a therapy checklist to <span className="text-trital-navy">begin.</span>
      </>
    ),
    sub: "Setting-aware checklists for PT, OT, SLP, and Respiratory Therapy across acute, inpatient rehab, SNF, home health, pediatric, and school-based.",
  },
  other: {
    Icon: Microscope,
    eyebrow: "Allied Health Checklist",
    breadcrumb: "Allied & Other",
    heroAccent: "text-trital-navy",
    h1: (
      <>
        Pick an allied checklist to <span className="text-trital-navy">begin.</span>
      </>
    ),
    sub: "Imaging, Laboratory, Sterile Processing, Surgical Tech, Pharmacy, Medical Assistant, CNA, LPN, EMT, and beyond.",
  },
};

export default function ChecklistCatalog({ type }) {
  const meta = TYPE_META[type];
  const group = CHECKLISTS[type];

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const categories = useMemo(() => {
    const set = new Set();
    group.items.forEach((c) => set.add(c.category));
    return ["all", ...Array.from(set).sort()];
  }, [group]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return group.items.filter((c) => {
      if (category !== "all" && c.category !== category) return false;
      if (!q) return true;
      return (
        c.title.toLowerCase().includes(q) ||
        c.summary.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q)
      );
    });
  }, [group, query, category]);

  const Icon = meta.Icon;

  return (
    <div className="min-h-screen bg-white" data-testid={`checklist-catalog-${type}`}>
      <PageMeta
        title={`${group.label} | TRITAL Care®`}
        description={`${group.label} — TRITAL Care® specialty competency checklists. ${meta.sub}`}
        canonical={`https://www.tritalcare.com/compliance/checklists/${type}`}
      />
      <PublicNav />

      {/* HERO ------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid={`catalog-${type}-hero`}>
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-12 lg:pt-20 lg:pb-16">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance" className="hover:text-trital-navy">Compliance</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance/checklists" className="hover:text-trital-navy">Checklists</Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">{meta.breadcrumb}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <Icon className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">{meta.eyebrow}</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl lg:text-[56px] leading-[1.04] tracking-tight text-slate-900 text-balance">
                {meta.h1}
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">{meta.sub}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12.5px] text-slate-700">
                  <ClipboardCheck className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                  {group.items.length} checklists
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12.5px] text-slate-700">
                  <Save className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                  Save & resume later
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12.5px] text-slate-700">
                  <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                  Private by default
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-trital-medical-blue-soft blur-3xl" />
                <div className="relative">
                  <div className="overline text-trital-red">Rating scale</div>
                  <ul className="mt-4 space-y-2.5 text-[13px] text-slate-700">
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-slate-300 flex items-center justify-center text-white text-[10.5px] font-bold">1</span>
                      No experience
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-amber-400 flex items-center justify-center text-white text-[10.5px] font-bold">2</span>
                      Need training
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-emerald-500 flex items-center justify-center text-white text-[10.5px] font-bold">3</span>
                      Able to perform with supervision
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-trital-navy flex items-center justify-center text-white text-[10.5px] font-bold">4</span>
                      Able to perform independently
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RECRUITER REVIEW NOTICE ----------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-4">
        <div
          className="rounded-2xl border border-trital-navy/15 bg-trital-medical-blue-soft/60 px-5 py-4 flex items-start gap-3"
          data-testid={`catalog-${type}-recruiter-notice`}
        >
          <ShieldCheck className="w-5 h-5 text-trital-navy mt-0.5 shrink-0" strokeWidth={1.8} />
          <div className="text-[13.5px] text-slate-700 leading-relaxed">
            <span className="font-semibold text-trital-navy">Recruiter review, not a test.</span> A named TRITAL Care® recruiter
            reviews every submission, adds verification notes, and confirms any specialty before a facility sign-off. Choose
            the checklist that best matches your most recent role.
          </div>
        </div>
      </section>

      {/* TOOLBAR --------------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {/* Category chips */}
        {categories.length > 2 && (
          <div
            className="flex flex-wrap gap-2 mb-5"
            role="tablist"
            aria-label="Filter by category"
            data-testid={`catalog-${type}-category-filter`}
          >
            {categories.map((cat) => {
              const active = category === cat;
              const count =
                cat === "all"
                  ? group.items.length
                  : group.items.filter((i) => i.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-[12.5px] font-semibold transition-all ${
                    active
                      ? "bg-trital-navy text-white border-trital-navy"
                      : "bg-white text-slate-700 border-slate-300 hover:border-trital-navy/40"
                  }`}
                  data-testid={`catalog-${type}-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {cat === "all" ? "All" : cat}
                  <span className={`ml-1 text-[11px] font-bold ${active ? "text-white/80" : "text-slate-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1 max-w-xl">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.8} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${group.items.length} ${type === "other" ? "allied" : type} checklists`}
              className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-300 bg-white text-[14px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-trital-navy/20 focus:border-trital-navy"
              aria-label="Search checklists"
              data-testid={`catalog-${type}-search`}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            )}
          </div>
          <div className="text-[12.5px] text-slate-500">
            {filtered.length} of {group.items.length} shown
          </div>
        </div>
      </section>

      {/* CARD GRID ------------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12" data-testid={`catalog-${type}-grid`}>
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <AlertCircle className="w-5 h-5 text-trital-navy mx-auto" strokeWidth={1.8} />
            <div className="mt-3 font-display font-bold text-[15px] text-slate-900">No matches.</div>
            <div className="mt-1 text-[13px] text-slate-500">
              Try a broader term or{" "}
              <button
                onClick={() => {
                  setQuery("");
                  setCategory("all");
                }}
                className="text-trital-navy font-semibold hover:underline"
              >
                clear filters
              </button>
              .
            </div>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item, i) => {
              const totalQ = item.sections.reduce((n, s) => n + s.questions.length, 0);
              return (
                <motion.li
                  key={item.slug}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.02, 0.2) }}
                  data-testid={`catalog-${type}-card-${item.slug}`}
                >
                  <Link
                    to={`/compliance/checklists/${type}/${item.slug}`}
                    className="group relative h-full block rounded-2xl bg-white border border-slate-200 p-5 lg:p-6 shadow-card hover:-translate-y-0.5 hover:shadow-card-hover transition-all overflow-hidden"
                  >
                    <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-trital-medical-blue-soft blur-2xl opacity-60 group-hover:opacity-100 transition-opacity" />
                    <div className="relative">
                      <div className="flex items-start justify-between gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-trital-navy text-[10.5px] font-bold tracking-wider uppercase">
                          {item.category}
                        </span>
                        <ArrowUpRight
                          className="w-4 h-4 text-trital-navy opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                          strokeWidth={1.8}
                        />
                      </div>
                      <h3 className="mt-3 font-display font-bold text-[18px] leading-snug text-slate-900 group-hover:text-trital-navy">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-[13px] text-slate-600 leading-relaxed line-clamp-3">
                        {item.summary}
                      </p>
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-[11.5px] font-semibold text-slate-500">
                          {item.sections.length} sections · {totalQ} items
                        </span>
                        <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-trital-navy">
                          Open <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.li>
              );
            })}
          </ul>
        )}
      </section>

      {/* CTA BAND --------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-navy-ink text-white" data-testid={`catalog-${type}-cta-band`}>
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-[1.3fr_1fr] items-center gap-8">
          <div>
            <div className="overline text-trital-red flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Ready when you are
            </div>
            <h3 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-balance text-white">
              One checklist. Every placement.
            </h3>
            <p className="mt-3 text-white/80 max-w-[58ch] leading-relaxed">
              Pick a specialty, score your competency on a 1–4 scale, attest, and submit. A named TRITAL Care® recruiter reviews
              every submission within one business day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[280px]">
            <Link
              to="/compliance/checklists"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100"
              data-testid={`catalog-${type}-cta-back`}
            >
              All checklist categories
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid={`catalog-${type}-cta-recruiter`}
            >
              Talk to a recruiter <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid={`catalog-${type}-sms`}>
        <SMSRecruiterCard variant="default" headline={SMS_COPY.checklist.headline} subtext={SMS_COPY.checklist.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
